import get from 'lodash/get'

// $select keeps the payload to the one field identify needs — no full profile in transit
const GRAPH_ME_MAIL_URL = 'https://graph.microsoft.com/v1.0/me?$select=mail'

// MS Graph /me is the source of truth for the real mailbox address: for tenants with short
// UPNs (e.g. azucarperu.com.pe) the MSAL account `username` is the UPN (aperezd@…), not the
// email (alejandra.perez@…). The User.Read scope already requested covers this call, so no
// scope/consent change is needed. Resolves null on any failure — identify must still fire
// with the remaining azure traits when Graph is unreachable.
export const fetchAzureGraphMail = async accessToken => {
  try {
    const response = await fetch(GRAPH_ME_MAIL_URL, {
      headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' }
    })
    if (!response.ok) return null
    const profile = await response.json()
    return get(profile, 'mail') || null
  } catch (e) {
    return null
  }
}

// The sign-in description an MSAL account gives the platform session (userStoredData). `oid` is the
// Entra object id claim ONLY: MSAL's localAccountId falls back to `sub`, which is per-application
// and would split one person across web and mobile — an account without `oid` is simply an Azure
// session without one. `mail` starts as the `email` ID-token claim when the tenant emits it (so
// identify has an azureMail from the first call) and is replaced by the Graph mailbox address once
// that resolves. No `preferred_username` fallback: that claim IS the UPN (#1412). Never the Graph
// access token.
export const buildAzureAuth = account => ({
  oid: get(account, ['idTokenClaims', 'oid']),
  name: get(account, 'name'),
  upn: get(account, 'username'),
  mail: get(account, ['idTokenClaims', 'email'])
})

// Patches the real mailbox address into the Azure session once MS Graph resolves; without one the
// session keeps the ID-token claim buildAzureAuth seeded. Graph is queried in parallel with the hemav
// login, but the patch waits for that login (`loginDone`): the session cookie is written inside
// receiveLogin, before the profile fetch, so the identify effect (deps [user, auth]) never observes
// `user` before the Azure session exists — a slow Graph only delays the Graph address (Segment merges
// the re-identify). Resolves once done; never rejects.
export const resolveAzureAccountMail = ({ accessToken, session, loginDone }) =>
  Promise.all([fetchAzureGraphMail(accessToken), loginDone])
    .then(([graphMail]) => {
      if (graphMail) session.setAuth({ mail: graphMail })
    })
    .catch(() => {})

// Web twin of mobile's buildIdentifyTraits, read from the session cookie so the Azure traits survive
// reloads and new tabs. `email` deliberately keeps carrying the UPN so existing Segment/PowerBI joins
// keep working; the real mailbox address travels in the `azureMail` trait. Credentials sessions (and
// no session description at all) keep today's traits exactly.
export const buildIdentifyProps = ({ user, auth }) => {
  const isAzure = get(auth, 'method') === 'azure'

  return {
    username: isAzure ? get(auth, 'name') : get(user, 'username'),
    email: isAzure ? get(auth, 'upn') : get(user, 'email'),
    roles: get(user, 'roles'),
    azureId: isAzure ? get(auth, 'oid') : undefined,
    azureMail: isAzure ? get(auth, 'mail') : undefined,
    platform: 'web'
  }
}
